/**
 * ConsentPanel — what the organization may do with a source's content, and the form that records it.
 * Template example: NextAdmin `profile/security/page.tsx` card with a settings form; demo `/form-layouts`.
 */
import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import type { IntelSource, IntelSourceConsentRequest } from '../../../generated/api/types.gen.js';
import { zIntelSourceConsentRequest } from '../../../generated/api/zod.gen.js';
import { recordIntelSourceConsentMutation } from '../../../generated/api/@tanstack/react-query.gen.js';
import { Can } from '../../../components/auth/Can.js';
import { Card } from '../../../components/ui';
import { Form, SelectFormField, SubmitButton, TextFormField, useZodForm } from '../../../components/form';
import { useIntelSourceInvalidation } from './intel-source-queries.js';

type ConsentKind = IntelSourceConsentRequest['kind'];

const KIND_LABELS: Record<ConsentKind, string> = {
  none: 'None (unlicensed public-content simulation)',
  creator_licence: 'Creator licence',
  platform_permission: 'Platform permission',
};

const KIND_OPTIONS = (Object.keys(KIND_LABELS) as ConsentKind[]).map((value) => ({ value, label: KIND_LABELS[value] }));

const NO_CONSENT_LINE = 'Unlicensed public-content simulation';

/** A licence or permission needs its reference; `none` does not. Returns the form error, or null when valid. */
export function consentFormError(kind: ConsentKind, reference: string): string | null {
  if (kind !== 'none' && reference.trim() === '') return 'A reference is required for a licence or permission.';
  return null;
}

const consentSchema = zIntelSourceConsentRequest.refine(
  (value) => consentFormError(value.kind, value.reference ?? '') === null,
  {
    message: 'A reference is required for a licence or permission.',
    path: ['reference'],
  },
);

export const ConsentPanel: React.FC<{ source: Pick<IntelSource, 'id' | 'consent'> }> = ({ source }) => {
  const invalidate = useIntelSourceInvalidation();
  const form = useZodForm(consentSchema, { defaultValues: { kind: 'creator_licence', reference: '' } });
  const kind = form.watch('kind');

  const mutation = useMutation({
    ...recordIntelSourceConsentMutation(),
    onSuccess: () => {
      invalidate(source.id);
      form.reset({ kind: 'creator_licence', reference: '' });
    },
  });

  return (
    <Card title="Consent">
      <p className="flex items-start gap-2 text-sm text-body">
        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-muted" aria-hidden="true" />
        {source.consent?.line ?? NO_CONSENT_LINE}
      </p>
      {/* The bar of approving a persona: agents and the workforce (the route requires both). */}
      <Can policy="CanManageAgents">
        <Can policy="CanManageWorkforce">
          <Form
            form={form}
            className="mt-5 gap-4"
            onSubmit={(values) =>
              mutation.mutateAsync({
                path: { id: source.id },
                body: { kind: values.kind, reference: values.kind === 'none' ? null : (values.reference ?? '').trim() },
              })
            }
          >
            <SelectFormField name="kind" label="Consent kind" options={KIND_OPTIONS} required />
            <TextFormField
              name="reference"
              label="Reference"
              placeholder="Licence id or permission link"
              disabled={kind === 'none'}
            />
            <div>
              <SubmitButton variant="primary" size="md">
                Record consent
              </SubmitButton>
            </div>
          </Form>
        </Can>
      </Can>
    </Card>
  );
};
